import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, CheckCircle2, Mail } from "lucide-react";
import Reveal from "@/components/Reveal";
import content from "@/content/siteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", email: "", company: "", message: "", consent: false, website: "" };

export default function ContactSection() {
  const c = content.contact;
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Please accept the consent checkbox so we can reply to you.");
      return;
    }
    setStatus("sending");
    try {
      await axios.post(`${API}/leads`, form);
      setStatus("success");
      toast.success("Inquiry sent — talk soon!");
    } catch (err) {
      setStatus("idle");
      const code = err?.response?.status;
      toast.error(
        code === 429
          ? "Too many attempts — please wait a few minutes and try again."
          : "Something went wrong sending your message. Please try again."
      );
    }
  };

  const inputCls =
    "w-full rounded-xl border border-plz-line bg-plz-navy/60 px-4 py-3 text-base text-plz-cream placeholder:text-plz-muted transition-colors duration-300 focus:border-plz-orange focus:outline-none focus:ring-1 focus:ring-plz-orange";

  return (
    <section id={c.id} data-testid="closing-cta" className="relative scroll-mt-24 overflow-hidden border-t border-plz-line/60">
      <div className="pointer-events-none absolute left-1/2 top-0 h-80 w-[42rem] -translate-x-1/2 rounded-full bg-plz-orange/10 blur-3xl" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-plz-orange">{c.label}</p>
            <h2 className="mt-4 font-heading text-2xl font-bold leading-snug tracking-tight sm:text-3xl lg:text-4xl">
              {c.headlineA}
              <span className="text-plz-orange">{c.headlineAccent}</span>
              {c.headlineB}
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-plz-slate sm:text-lg">{c.intro}</p>
            <a
              href={`mailto:${content.footer.contactEmail}`}
              data-testid="contact-email-link"
              className="group mt-8 inline-flex items-center gap-3 font-heading text-sm font-bold text-plz-orange transition-colors hover:text-plz-amber"
            >
              <Mail className="h-4 w-4" aria-hidden="true" />
              {content.footer.contactEmail}
            </a>
          </Reveal>

          <Reveal delay={0.15}>
            {status === "success" ? (
              <div data-testid="contact-success-panel" className="spotlight-frame flex h-full flex-col items-center justify-center rounded-3xl bg-plz-surface/50 p-10 text-center">
                <CheckCircle2 className="h-14 w-14 text-plz-orange" aria-hidden="true" />
                <h3 className="mt-6 font-heading text-2xl font-semibold">{c.successTitle}</h3>
                <p className="mt-3 max-w-sm text-base leading-relaxed text-plz-slate">{c.successMessage}</p>
                <button
                  data-testid="contact-send-another-btn"
                  onClick={() => {
                    setForm(EMPTY);
                    setStatus("idle");
                  }}
                  className="mt-8 rounded-full border border-plz-line px-6 py-3 font-heading text-sm font-bold text-plz-cream transition-colors hover:border-plz-orange/60 hover:bg-plz-surface/60"
                >
                  {c.successCta}
                </button>
              </div>
            ) : (
              <form
                data-testid="contact-inquiry-form"
                onSubmit={onSubmit}
                className="spotlight-frame rounded-3xl bg-plz-surface/50 p-8 lg:p-10"
                noValidate={false}
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-plz-muted">
                      {c.fields.name} *
                    </label>
                    <input id="contact-name" data-testid="contact-input-name" required maxLength={120}
                      value={form.name} onChange={set("name")} className={inputCls} placeholder="Jesse Quick" />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-plz-muted">
                      {c.fields.email} *
                    </label>
                    <input id="contact-email" type="email" data-testid="contact-input-email" required
                      value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" />
                  </div>
                </div>
                <div className="mt-5">
                  <label htmlFor="contact-company" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-plz-muted">
                    {c.fields.company}
                  </label>
                  <input id="contact-company" data-testid="contact-input-company" maxLength={160}
                    value={form.company} onChange={set("company")} className={inputCls} placeholder="Acme Wellness Co." />
                </div>
                <div className="mt-5">
                  <label htmlFor="contact-message" className="mb-2 block font-mono text-xs uppercase tracking-[0.2em] text-plz-muted">
                    {c.fields.message} *
                  </label>
                  <textarea id="contact-message" data-testid="contact-input-message" required rows={5} maxLength={5000}
                    value={form.message} onChange={set("message")} className={`${inputCls} resize-y`}
                    placeholder="Tell us where AI could take the busywork off your plate…" />
                </div>

                {/* Honeypot — invisible to humans */}
                <div className="absolute left-[-9999px] top-[-9999px]" aria-hidden="true">
                  <label htmlFor="contact-website">Website</label>
                  <input id="contact-website" data-testid="contact-honeypot" type="text" tabIndex={-1}
                    autoComplete="off" value={form.website} onChange={set("website")} />
                </div>

                <label htmlFor="contact-consent" className="mt-6 flex cursor-pointer items-start gap-3">
                  <input id="contact-consent" type="checkbox" data-testid="contact-input-consent"
                    checked={form.consent} onChange={set("consent")}
                    className="mt-1 h-4 w-4 shrink-0 cursor-pointer rounded accent-[#4169e1]" />
                  <span className="text-sm leading-relaxed text-plz-slate">
                    {c.consentLabel}{" "}
                    <Link to="/privacy" data-testid="privacy-policy-link" className="text-plz-orange underline underline-offset-4 transition-colors hover:text-plz-amber">
                      {c.privacyLinkText}
                    </Link>
                  </span>
                </label>

                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={status === "sending"}
                  className="group mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-plz-orange px-8 py-4 font-heading text-sm font-bold text-plz-cream transition-[transform,background-color,box-shadow,opacity] duration-300 hover:-translate-y-0.5 hover:bg-plz-ember hover:shadow-[0_12px_40px_rgba(65,105,225,0.35)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === "sending" ? (
                    <>
                      {c.sendingLabel}
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    </>
                  ) : (
                    <>
                      {c.submitLabel}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                    </>
                  )}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
